(() => {
  'use strict';

  const ZOHO_FORM_URL =
    'https://forms.zohopublic.eu/web10/form/SaveYourBasket/formperma/IWJ-vuzBjz31V5flglkL3DemaeGDYpOhK8I8SR8bl1Q';

  const selectors = {
    root: '[data-save-cart]',
    open: '[data-save-cart-open]',
    close: '[data-save-cart-close]',
    modal: '[data-save-cart-modal]',
    iframe: '[data-save-cart-iframe]',
    loading: '[data-save-cart-loading]',
    error: '[data-save-cart-error]'
  };

  let currentRoot = null;
  let hasLoadedOnce = false; // avoid re-fetching cart + re-loading iframe every open

  function formatMoney(amountInMinorUnits, currency = 'GBP') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency
    }).format(amountInMinorUnits / 100);
  }

  function getShopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function getCart() {
    const response = await fetch(`${getShopRoot()}cart.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  function buildCartLink(cart) {
    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      throw new Error('Cart is empty.');
    }

    const cartItems = cart.items.map((item) => {
      const variantId = Number(item.variant_id);
      const quantity = Number(item.quantity);

      if (
        !Number.isSafeInteger(variantId) ||
        variantId <= 0 ||
        !Number.isInteger(quantity) ||
        quantity <= 0
      ) {
        throw new Error('The cart contains an invalid item.');
      }

      return `${variantId}:${quantity}`;
    });

    const SHOP_DOMAIN = 'yo-yodesk.co.uk';

    return `${SHOP_DOMAIN}/cart/${cartItems.join(',')}?utm_source=saved_cart&utm_medium=email&utm_campaign=save_cart`;
  }

  function buildCartSummary(cart) {
    return cart.items
      .map((item) => {
        const productName = item.product_title || item.title || 'Product';
        const variantName =
          item.variant_title && item.variant_title !== 'Default Title'
            ? ` – ${item.variant_title}`
            : '';

        return `${item.quantity} × ${productName}${variantName}`;
      })
      .join('\n');
  }

  function buildZohoFormUrl(cart) {
    const url = new URL(ZOHO_FORM_URL);

    url.searchParams.set('cart_link', buildCartLink(cart));
    url.searchParams.set('cart_summary', buildCartSummary(cart));
    url.searchParams.set(
      'cart_total',
      formatMoney(cart.total_price, cart.currency || 'GBP')
    );
    url.searchParams.set('cart_currency', cart.currency || 'GBP');
    url.searchParams.set('cart_count', String(cart.item_count || 0));
    url.searchParams.set('cart_token', cart.token || '');
    url.searchParams.set('page_url', window.location.href);
    url.searchParams.set('saved_at', new Date().toISOString());
    url.searchParams.set('source', 'Shopify Save Cart');

    return url.toString();
  }

  function openSaveCartModal(root) {
    const modal = root.querySelector(selectors.modal);
    if (!modal) return;

    modal.hidden = false;
    document.body.classList.add('yy-save-cart-open');
    currentRoot = root;

    if (!hasLoadedOnce) {
      prepareForm(root);
      hasLoadedOnce = true;
    }
  }

  function closeSaveCartModal(root) {
    const modal = root.querySelector(selectors.modal);
    if (!modal) return;

    modal.hidden = true;
    document.body.classList.remove('yy-save-cart-open');
  }

  function openThankYouModal() {
    const modal = document.querySelector('[data-thankyou-modal]');
    if (!modal) return;

    // Close the save-cart modal behind it
    if (currentRoot) {
      closeSaveCartModal(currentRoot);
    }

    modal.hidden = false;
    document.body.classList.add('yy-thankyou-open');
    modal.querySelector('[data-thankyou-close]')?.focus();
  }

  function closeThankYouModal() {
    const modal = document.querySelector('[data-thankyou-modal]');
    if (modal) {
      modal.hidden = true;
      document.body.classList.remove('yy-thankyou-open');
    }

    // Rebuild the save-cart form fresh so it's ready to use again next time
    hasLoadedOnce = false;
    if (currentRoot) {
      prepareForm(currentRoot);
      hasLoadedOnce = true;
    }
  }

  async function prepareForm(root) {
    const iframe = root.querySelector(selectors.iframe);
    const loading = root.querySelector(selectors.loading);
    const error = root.querySelector(selectors.error);

    if (!iframe || !loading || !error) {
      console.error('Save Cart: required modal elements were not found.');
      return;
    }

    // Clear any stale handler from a previous run BEFORE touching src
    iframe.onload = null;
    iframe.onerror = null;

    loading.hidden = false;
    error.hidden = true;
    iframe.hidden = true;
    iframe.removeAttribute('src');
    iframe.classList.remove('yy-save-cart__form--visible');

    try {
      console.log('Save Cart: requesting current cart…');

      const cart = await getCart();

      console.log('Save Cart: cart received:', cart);

      if (!cart.items || cart.items.length === 0) {
        throw new Error('The cart is empty.');
      }

      const zohoUrl = buildZohoFormUrl(cart);

      console.log('Save Cart: Zoho form URL:', zohoUrl);

      /*
       * Track how many times the iframe has loaded. The first load is
       * the empty Zoho form itself. Zoho's "thank you" page is shown
       * by navigating the SAME iframe internally after the form is
       * submitted, which fires another 'load' event on the iframe.
       * From the second load onward, treat it as submission complete —
       * hide the iframe and show our own popup instead of Zoho's page.
       */
      let loadCount = 0;

      iframe.onload = () => {
        loadCount += 1;
        loading.hidden = true;

        if (loadCount > 1) {
          iframe.hidden = true;
          openThankYouModal();
        } else {
          iframe.hidden = false;
          requestAnimationFrame(() => {
            iframe.classList.add('yy-save-cart__form--visible');
          });
        }
      };

      iframe.onerror = () => {
        console.error('Save Cart: Zoho iframe failed to load.');
        loading.hidden = true;
        iframe.hidden = true;
        error.hidden = false;
      };

      iframe.hidden = false;
      iframe.src = zohoUrl;

      window.setTimeout(() => {
        if (!loading.hidden) {
          console.error('Save Cart: iframe loading timed out.');
          loading.hidden = true;
          error.hidden = false;
        }
      }, 15000);
    } catch (err) {
      console.error('Save Cart error:', err);

      loading.hidden = true;
      iframe.hidden = true;
      error.hidden = false;

      error.textContent =
        err instanceof Error
          ? `We could not prepare your saved cart: ${err.message}`
          : 'We could not prepare your saved cart. Please try again.';
    }
  }

  // Wire up open/close triggers for each save-cart block on the page
  document.querySelectorAll(selectors.root).forEach((root) => {
    root.querySelectorAll(selectors.open).forEach((btn) => {
      btn.addEventListener('click', () => openSaveCartModal(root));
    });

    root.querySelectorAll(selectors.close).forEach((btn) => {
      btn.addEventListener('click', () => closeSaveCartModal(root));
    });
  });

  // Thank-you modal close triggers (shared, outside root loop since it's a single global modal)
  document.querySelectorAll('[data-thankyou-close]').forEach((el) => {
    el.addEventListener('click', closeThankYouModal);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const thankYouModal = document.querySelector('[data-thankyou-modal]');
    if (thankYouModal && !thankYouModal.hidden) {
      closeThankYouModal();
      return;
    }

    if (currentRoot) {
      const saveCartModal = currentRoot.querySelector(selectors.modal);
      if (saveCartModal && !saveCartModal.hidden) {
        closeSaveCartModal(currentRoot);
      }
    }
  });
})();